import { Inject, Injectable } from '@angular/core';
import { DashboardEventLogCriteria, standardGetHttpOptions, standardHttpOptions } from '../../shared/models';
import { LoadingService } from '../../shared/services/loading.service';
import { Observable, throwError } from 'rxjs';
import { finalize, catchError, map } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DashboardRequest } from '../../shared/models/Dashboard/dashboardRequest.model';
import { KigSettings, KIG_SETTINGS } from '../../../settings/kig-settings';
import { UserService } from '@services/user.service';
import { of } from 'rxjs';


@Injectable()
export class DashboardEventsService {
  public standardHttpOptionsWithUser: any;

  private apiUrl: string;

  constructor(private http: HttpClient,
          private loadingService: LoadingService,
          @Inject(KIG_SETTINGS) private settings: KigSettings,
          private userService: UserService) {
    this.apiUrl = this.settings.apiUrl;
  }

  buildStandardHttpOptionsWithUser(username: string) {
    this.standardHttpOptionsWithUser = {
      withCredentials: true,
      headers: new HttpHeaders({
        ['Content-Type']: 'application/json',
        username
      })
    };
  }

  public getDashboards(): Observable<any> {
    this.loadingService.setMessage('Loading dashboards ...');
    return this.http.get(`${this.apiUrl}/dashboard`,  standardGetHttpOptions).pipe(
      map((response: any) => response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public getDashboardEventValues(dashboardCriteria: DashboardEventLogCriteria): Observable<any> {
    this.loadingService.setMessage('Loading event values ...');
    return this.http.post(`${this.apiUrl}/event/DashboardEventConditionValues`, JSON.stringify(dashboardCriteria), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getDashboardEventLogs(dashboardCriteria: DashboardEventLogCriteria): Observable<any> {
    this.loadingService.setMessage('Loading dashboard events ...');
    return this.http.post(`${ this.apiUrl }/event/dashboardeventlogs`, JSON.stringify(dashboardCriteria), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getEventIdsWithNoRecentEventLog(myEventIds: number[]): Observable<any> {
    return this.http.post(`${ this.apiUrl }/dashboard/EventIdsWithNoRecentEventLog`, JSON.stringify(myEventIds), standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public getEventIdsForActionsWithNegativeResponse(myEventIds: number[]): Observable<any> {
    return this.http.post(`${ this.apiUrl }/dashboard/EventIdsForActionsWithHangingOrFailingResponses`, JSON.stringify(myEventIds), standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }
  

  public getIrrigationEventsLeftOn(): Observable<any> {
    return this.http.get(`${ this.apiUrl }/dashboard/IrrigationEventsLeftOn`, standardGetHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  // TODO update Observable<any> to accurately reflect returned data type
  public createDashboard(dashboardRequest: DashboardRequest): Observable<any> {
    const body = JSON.stringify(dashboardRequest);
    this.loadingService.setMessage('Creating dashboard ...');
    return this.http.post(`${this.apiUrl}/dashboard`,body, standardHttpOptions).pipe(
      map((response: Response) => {
        if (response==null) {
          return 'success';
        }
        return response;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public updateDashboard(dashboardRequest: DashboardRequest, username: string) {
    const body = JSON.stringify(dashboardRequest);
    this.loadingService.setMessage('Updating dashboard ...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.put(`${this.apiUrl}/dashboard/`,body, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => {
        if (response == null) {
          return 'success';
        }
        return response;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  // Save the Favorites-tab tile order (no loading overlay: fires on every drop).
  public updateFavoritesOrder(dashboardId: number, tileKeys: string[], username: string) {
    const body = JSON.stringify(tileKeys);
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.put(`${this.apiUrl}/dashboard/${dashboardId}/favoritesorder`, body, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => {
        if (response == null) {
          return 'success';
        }
        return response;
      }),
      catchError(this.handleError)
    );
  }


  public deleteDashboard(dashboardId: number, username: string) {
    this.loadingService.setMessage('Deleting dashboard ...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.delete(`${this.apiUrl}/dashboard/${dashboardId}`, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => {
        if (response == null) {
          return 'success';
        }
        return response;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  
  public generateDashboardEventActionSummaries(lastXMinutes: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/sensorreading/ActionSumsForDashboardEvents/${lastXMinutes}`,  standardGetHttpOptions).pipe(
      map((response: any) => response),
      catchError(this.handleError));
  }


  private handleError(error: any) {
    if (error.status === 404) {
      return of(null);
    }
    return throwError(error);
  }
}
